/**
 * Frontend Template Component - Case Study Page 2
 * 
 * Renders the second page of the case study using JavaScript template literals
 * Matches the backend Jinja2 template exactly for consistent output.
 */

import { escapeHtml, formatInlineBold } from '../utils/inlineTextFormat.js'
import type { CaseStudy, TextSizingConfig } from '../types/caseStudy'
import type { EditorLayout, SectionKey } from '../types/editor'

const getTemplateImagesBaseUrl = (): string => {
  const bucketUrl = (typeof import.meta !== 'undefined' && import.meta.env) 
    ? import.meta.env.VITE_TEMPLATE_IMAGES_BASE_URL 
    : undefined;
  
  if (bucketUrl) {
    return bucketUrl;
  }
  
  return 'https://case-study-prod-template-images-1e62f03a.s3.us-east-2.amazonaws.com';
};

const getSmallSize = (section: string): string => {
  const sizes: Record<string, string> = {
    partnership_client_overview: '0.7rem',
    partnership_bullets: '0.6rem',
    why_choose_riscpoint: '0.65rem',
    testimonial: '0.75rem',
    call_to_action: '0.7rem'
  };
  return sizes[section] || '0.65rem';
};

const getLargeSize = (section: string): string => {
  const sizes: Record<string, string> = {
    partnership_client_overview: '0.9rem',
    partnership_bullets: '0.8rem',
    why_choose_riscpoint: '0.85rem',
    testimonial: '0.95rem',
    call_to_action: '0.9rem'
  };
  return sizes[section] || '0.85rem';
};

const getSmallLineHeight = (section: string): string => {
  const lineHeights: Record<string, string> = {
    partnership_client_overview: '1.3',
    partnership_bullets: '1.2',
    why_choose_riscpoint: '1.2',
    testimonial: '1.3',
    call_to_action: '1.2'
  };
  return lineHeights[section] || '1.2';
};

const getLargeLineHeight = (section: string): string => {
  const lineHeights: Record<string, string> = {
    partnership_client_overview: '1.5',
    partnership_bullets: '1.4',
    why_choose_riscpoint: '1.4',
    testimonial: '1.5',
    call_to_action: '1.4'
  };
  return lineHeights[section] || '1.4';
};

const generateTextSizingCSS = (textSizingConfig: TextSizingConfig = {} as TextSizingConfig): string => {
  if (!textSizingConfig || Object.keys(textSizingConfig).length === 0) {
    return '';
  }

  let cssOverrides = '/* Dynamic sizing overrides */\n:root {\n';
  
  Object.entries(textSizingConfig).forEach(([section, config]) => {
    const cssVar = section.replace(/_/g, '-');
    
    if (config === 'small') {
      cssOverrides += `  --${cssVar}-font-size: ${getSmallSize(section)};\n`;
      cssOverrides += `  --${cssVar}-line-height: ${getSmallLineHeight(section)};\n`;
    } else if (config === 'large') {
      cssOverrides += `  --${cssVar}-font-size: ${getLargeSize(section)};\n`;
      cssOverrides += `  --${cssVar}-line-height: ${getLargeLineHeight(section)};\n`;
    } else if (config === 'auto-fit') {
      const computedKey = `${section}_computed` as keyof typeof textSizingConfig;
      if (textSizingConfig[computedKey]) {
        cssOverrides += `  --${cssVar}-font-size: ${textSizingConfig[computedKey]};\n`;
      }
    } else if (typeof config === 'string' && config.endsWith('rem')) {
      cssOverrides += `  --${cssVar}-font-size: ${config};\n`;
    }
  });

  cssOverrides += '}\n';
  return cssOverrides;
};

const generateSpacingCSS = (layout?: EditorLayout): string => {
  if (!layout?.sectionSpacing) return ''
  let css = ''
  Object.entries(layout.sectionSpacing).forEach(([key, spacing]) => {
    const s = spacing as { marginTop: number; marginBottom: number }
    if (s.marginTop === 0 && s.marginBottom === 0) return
    css += `[data-section-key="${key}"] { margin-top: ${s.marginTop}in !important; margin-bottom: ${s.marginBottom}in !important; }\n`
  })
  return css
}

const PREVIEW_INTERACT_CSS = `
[data-preview-edit] {
  cursor: pointer;
  transition: outline-color 0.12s ease, outline-offset 0.12s ease, background-color 0.12s ease;
  border-radius: 4px;
}
[data-preview-edit]:hover {
  outline: 2px solid rgba(37, 99, 235, 0.6);
  outline-offset: 2px;
  background-color: rgba(37, 99, 235, 0.04);
}
`

interface TemplateDataPage2 {
  title: string;
  subtitle: string;
  client_overview: string;
  partnership_details: Array<{ title: string; description: string }>;
  riscpoint_section: string;
  contact_us_section: string;
  client_testimonial: string;
  _partnerImage: string | null;
  _partnerImageSize: number;
  _partnerImageOffsetX: number;
  _partnerImageOffsetY: number;
  _graphicsLogos: {
    logo1: string | null;
    logo2: string | null;
    logo1Size: number;
    logo2Size: number;
    logo1OffsetX: number;
    logo1OffsetY: number;
    logo2OffsetX: number;
    logo2OffsetY: number;
  } | null;
}

const convertCaseStudyDataPage2 = (caseStudy: CaseStudy): TemplateDataPage2 => {
  return {
    title: caseStudy.title,
    subtitle: caseStudy.subtitle,
    client_overview: caseStudy.partnership_details?.client_overview || 'RISCPoint delivers customized cybersecurity solutions that transform complex compliance challenges into competitive advantages.',
    partnership_details: caseStudy.partnership_details?.partnership_details || [],
    riscpoint_section: caseStudy.why_choose_riscpoint || 'We specialize in transforming complex financial services environments where security, performance, and compliance converge. Our deep understanding of both cloud technologies and financial industry requirements enables us to deliver seamless modernization initiatives that provide measurable business value. Our proven methodologies combine technical excellence with regulatory expertise, ensuring our clients achieve their operational objectives while maintaining the highest standards of security and compliance.',
    contact_us_section: caseStudy.call_to_action?.description || 'Transform your organization\'s technology capabilities with proven expertise in cloud modernization and regulatory compliance. Partner with us to accelerate your digital transformation while maintaining operational excellence and meeting the most stringent compliance requirements.',
    client_testimonial: caseStudy.testimonial?.quote || '',
    _partnerImage: (caseStudy as any)._partnerImage || null,
    _partnerImageSize: (caseStudy as any)._partnerImageSize || 100,
    _partnerImageOffsetX: (caseStudy as any)._partnerImageOffsetX || 0,
    _partnerImageOffsetY: (caseStudy as any)._partnerImageOffsetY || 0,
    _graphicsLogos: (caseStudy as any)._graphicsLogos || null,
  };
};

const renderPartnershipSection = (data: TemplateDataPage2, baseUrl: string): string => {
    return `      <!-- Section 1: Partnership Details (Two-Column Layout) -->
      <section class="full-width-section partnership-details-section" data-section-key="partnership_details">
        <div class="section-header">
          <h2>PARTNERSHIP DETAILS</h2>
        </div>
        <div class="partnership-content">
          <div class="partnership-left">
            <div class="client-highlight" data-preview-edit="partnership_client_overview">
              <p class="client-description">${formatInlineBold(data.client_overview)}</p>
            </div>
          </div>
            <div class="partnership-right">
            ${data._partnerImage ? `
              <img src="${data._partnerImage}" alt="Partner image" class="injected-partner-img" style="width: ${data._partnerImageSize}%; height: auto; transform: translate(calc(-50% + ${data._partnerImageOffsetX}px), calc(-50% + ${data._partnerImageOffsetY}px));" />
            ` : `
              <div class="placeholder-rectangle partnership-graphic-top">
                <p>Client Logo</p>
              </div>
            `}
          </div>
        </div>
        <!-- Full-width bullet points below the two-column section -->
        <div class="partnership-outcomes" style="margin-bottom: 0.02in;" data-preview-edit="partnership_bullets">
          ${data.partnership_details.length > 0 ? `
            <ul class="partnership-bullets">
            ${data.partnership_details.map(detail => `
              <li>
                <strong>${escapeHtml(detail.title)}:</strong> ${formatInlineBold(detail.description)}
              </li>
            `).join('')}
            </ul>
          ` : ''}
        </div>
      </section>`
}

const renderWhyChooseRISCPointSection = (data: TemplateDataPage2, baseUrl: string): string => {
    return `      <!-- Section 2: Why Choose RISCPoint & Placeholder Graphics Area (Centered) -->
      <section class="full-width-section middle-section" data-section-key="why_choose_riscpoint">
        <div class="two-column-section" style="width: 100%;">
          <div class="left-column">
            <div class="placeholder-graphics-area">
              <div class="graphics-logos-container">
                <div class="graphics-logo-slot graphics-logo-1">
                  ${data._graphicsLogos?.logo1 ? `
                    <img src="${data._graphicsLogos.logo1}" class="graphics-injected-img" style="height:${Math.round(60 * (data._graphicsLogos.logo1Size / 100))}px !important;width:auto !important;object-fit:contain !important;transform: translate(calc(-50% + ${data._graphicsLogos.logo1OffsetX || 0}px), calc(-50% + ${data._graphicsLogos.logo1OffsetY || 0}px)) !important;" />
                  ` : ''}
                </div>
                <div class="graphics-logo-slot graphics-logo-2">
                  ${data._graphicsLogos?.logo2 ? `
                    <img src="${data._graphicsLogos.logo2}" class="graphics-injected-img" style="height:${Math.round(60 * (data._graphicsLogos.logo2Size / 100))}px !important;width:auto !important;object-fit:contain !important;transform: translate(calc(-50% + ${data._graphicsLogos.logo2OffsetX || 0}px), calc(-50% + ${((data._graphicsLogos as any)?.logo2OffsetY || 0) + 80}px)) !important;" />
                  ` : ''}
                </div>
              </div>
            </div>
          </div>
          <div class="right-column-content">
            <div class="section-header">
              <h3>WHY CHOOSE RISCPOINT?</h3>
            </div>
            <div class="section-content">
              <p class="riscpoint-section-text">${formatInlineBold(data.riscpoint_section)}</p>
            </div>
          </div>
        </div>
      </section>`
}

const renderCallToActionSection = (data: TemplateDataPage2, baseUrl: string): string => {
    return `      <!-- Section 3: Ready to Transform (Anchored to Bottom) -->
      <section class="full-width-section contact-section-wrapper" data-section-key="call_to_action">
        <div class="contact-cta-section" data-preview-edit="call_to_action">
          <h2>Ready to Transform Your Security Posture?</h2>
          <p>${formatInlineBold(data.contact_us_section)}</p>
        </div>
      </section>`
}

type Page2SectionRenderer = (data: TemplateDataPage2, baseUrl: string) => string

const PAGE2_SECTION_RENDERERS: Partial<Record<SectionKey, Page2SectionRenderer>> = {
    partnership_details: (data, baseUrl) => renderPartnershipSection(data, baseUrl),
    why_choose_riscpoint: (data, baseUrl) => renderWhyChooseRISCPointSection(data, baseUrl),
    call_to_action: (data, baseUrl) => renderCallToActionSection(data, baseUrl),
}

export const renderPage2Template = (
  caseStudy: CaseStudy,
  textSizingConfig: TextSizingConfig = {} as TextSizingConfig,
  layout?: EditorLayout
): string => {
  const templateImagesBaseUrl = getTemplateImagesBaseUrl();
  const dynamicCSS = generateTextSizingCSS(textSizingConfig);
  const spacingCSS = generateSpacingCSS(layout);
  const templateData = convertCaseStudyDataPage2(caseStudy);

  const page2Sections: SectionKey[] = layout?.zones?.['page2'] || ['partnership_details', 'why_choose_riscpoint', 'call_to_action']
  const page2Html = page2Sections
    .filter(key => PAGE2_SECTION_RENDERERS[key])
    .map(key => PAGE2_SECTION_RENDERERS[key]!(templateData, templateImagesBaseUrl))
    .join('\n')

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <title>${escapeHtml(templateData.title)} - Page 2</title>
  <link href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;600;700&display=swap" rel="stylesheet">
<style>
        /* Dynamic Text Sizing CSS Variables */
        :root {
            /* Default font sizes (medium) */
            --title-font-size: 2.2rem;
            --subtitle-font-size: 1.2rem;
            --at-glance-font-size: 0.75rem;
            --key-metrics-font-size: 0.75rem;
            --challenges-font-size: 0.75rem;
            --objectives-font-size: 0.75rem;
            --outcomes-font-size: 0.9rem;
            /* Page 2 specific sections */
            --partnership-client-overview-font-size: 0.8rem;
            --partnership-bullets-font-size: 0.7rem;
            --why-choose-riscpoint-font-size: 0.75rem;
            --testimonial-font-size: 0.85rem;
            --call-to-action-font-size: 0.8rem;

            /* Line height adjustments based on font size */
            --title-line-height: 1.1;
            --subtitle-line-height: 1.2;
            --at-glance-line-height: 1.3;
            --key-metrics-line-height: 1.3;
            --challenges-line-height: 1.3;
            --objectives-line-height: 1.3;
            --outcomes-line-height: 1.2;
            /* Page 2 specific line heights */
            --partnership-client-overview-line-height: 1.4;
            --partnership-bullets-line-height: 1.3;
            --why-choose-riscpoint-line-height: 1.3;
            --testimonial-line-height: 1.4;
            --call-to-action-line-height: 1.3;
        }

        ${dynamicCSS}

        ${spacingCSS}
        ${PREVIEW_INTERACT_CSS}

        /* RISCPoint Template Styles */
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: 'Work Sans', sans-serif;
            background: white;
            color: #333;
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: flex-start;
        }

        .container {
            width: 8.5in;
            height: 11in;
            margin: 0;
            background: white;
            box-shadow: 0 0 30px rgba(0,0,0,0.15);
            border-radius: 0;
            overflow: hidden;
            position: relative;
            display: flex;
            flex-direction: column;
        }

        .header {
            background: white;
            color: #22559c;
            padding: 0.1in 0.3in 0.2in 0.3in;
            position: relative;
            overflow: hidden;
            height: 26%;
            flex-shrink: 0;
        }

        .header::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            width: 400px;
            height: 100%;
            background: rgba(255,255,255,0.1);
            clip-path: polygon(20% 0%, 100% 0%, 100% 100%, 0% 100%);
        }

        .logo {
            position: absolute;
            top: 5px;
            right: 10px;
            height: 163px;
            width: auto;
            z-index: 3;
            opacity: 0.08;
        }

        .header h1 {
            font-size: var(--title-font-size);
            font-weight: 800;
            margin-bottom: 0.05in;
            line-height: var(--title-line-height);
            position: relative;
            z-index: 2;
            max-width: 66%;
            color: #22559c;
            text-shadow: 0 1px 2px rgba(255,255,255,0.8);
            margin-top: 0;
            padding-top: 0;
        }

        .header .subtitle {
            font-size: var(--subtitle-font-size);
            font-style: italic;
            position: relative;
            z-index: 2;
            line-height: var(--subtitle-line-height);
            max-width: 66%;
            color: #22559c;
            opacity: 0.8;
        }

        .skyline {
            position: absolute;
            top: 54%;
            right: 0;
            height: 46%;
            width: 100%;
            object-fit: cover;
            object-position: right center;
            z-index: 1;
            opacity: 1;
        }

        .main-content {
            display: flex;
            flex-direction: column;
            height: 93%;
            background: white;
            box-shadow: inset 0 1px 0 rgba(0,0,0,0.1);
            overflow: hidden;
            padding: 0.2in 0.3in;
            padding-top: 0.15in;
            position: relative;
            justify-content: flex-start;
        }

        .full-width-section {
            width: 100%;
            margin-bottom: 0.1in;
            flex-shrink: 0;
        }

        /* New Page 2 Full-Width Section Styles */
        .partnership-details-section {
            background: white;
        }

        .partnership-content {
            display: flex;
            gap: 0.2in;
            align-items: stretch;
            padding: 0.05in 0.2in;
        }

        .partnership-left {
            flex: 2;
            display: flex;
            flex-direction: column;
            gap: 0.15in;
            padding-right: 0.2in; /* Add padding to constrain the yellow container */
        }

        .partnership-right {
            flex: 1;
            position: relative;
            overflow: hidden;
            border-radius: 8px;
        }

        .injected-partner-img {
            position: absolute !important;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            border-radius: 8px;
            object-fit: contain;
        }

        .client-description {
            font-size: var(--partnership-client-overview-font-size);
            line-height: var(--partnership-client-overview-line-height);
            color: #333;
            font-weight: 500;
            margin-bottom: 0.1in;
            margin-left: -20px !important; /* Align with main content left boundary */
            background: white;
            padding: 0.1in;
            border-radius: 8px;
            border-left: 4px solid #22559c;
        }

        .partnership-bullets {
            list-style: none;
            margin: 0;
            padding: 0;
        }

        .partnership-bullets li {
            font-size: var(--partnership-bullets-font-size);
            line-height: var(--partnership-bullets-line-height);
            color: #555;
            margin-bottom: 0.08in;
            position: relative;
            padding-left: 15px;
        }

        .partnership-bullets li::before {
            content: "•";
            position: absolute;
            left: 0;
            color: #22559c;
            font-weight: bold;
        }

        .partnership-bullets li strong {
            color: #111;
            font-weight: 700;
        }

        .partnership-bullets li > strong:first-of-type {
            color: #22559c;
        }

        

        .outcomes-grid {
            display: flex;
            flex-direction: column;
            gap: 0.15in;
            margin-top: 0.1in;
        }

        .outcome-bubble {
            background: white;
            border-radius: 10px;
            padding: 0.15in;
            border: 1px solid rgba(34,85,156,0.1);
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            width: 75%;
            margin: 0 auto;
        }

        .outcome-bubble h3 {
            font-size: var(--partnership-client-overview-font-size);
            font-weight: 600;
            color: #22559c;
            margin-bottom: 0.05in;
            line-height: var(--partnership-client-overview-line-height);
        }

        .outcome-bubble p {
            font-size: var(--partnership-client-overview-font-size);
            line-height: var(--partnership-client-overview-line-height);
            color: #555;
            margin: 0;
        }

        .why-riscpoint-section {
            background: white;
        }

.riscpoint-section-text {
            font-size: var(--why-choose-riscpoint-font-size);
            line-height: var(--why-choose-riscpoint-line-height);
            color: #555;
            /* rely on section-content padding to avoid mismatch between screen and print */
            position: relative;
            z-index: 2;
        }

        .section {
            margin-bottom: 0.05in;
            padding-bottom: 0.3in;
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
        }

        .section:last-child {
            border-bottom: none;
            margin-bottom: 0;
        }

        .section-header {
            margin-bottom: 0.02in;
            padding: 0 0.2in 0.12in 0.1in;
            border-bottom: 3px solid #22559c;
        }
        
        /* Clean up any remaining debug CSS - keeping section organized */
        
        /* Adjust WHY CHOOSE RISCPOINT underline to align with main content boundary */
        body .container .main-content .middle-section .right-column-content .section-header {
            width: 95% !important; /* Align underline with main content right edge */
            padding-right: 0 !important; /* Reset conflicting padding */
        }

        .section-header h2 {
            font-size: 1.3rem;
            font-weight: 900;
            color: #1a1a1a;
            letter-spacing: 0.5px;
            margin: 0;
        }

        .section-header h3 {
            font-size: 1.15rem;
            font-weight: 900;
            color: #1a1a1a;
            letter-spacing: 0.5px;
        }

        .dropdown-arrow {
            display: none;
        }

        .section-content {
            padding: 0.05in 0.2in 0.05in 0.1in;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
        }

        .section-content ul {
            list-style: none;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
        }

        .section-content li {
            font-size: var(--challenges-font-size);
            line-height: var(--challenges-line-height);
            margin-bottom: 0.01in;
            position: relative;
            padding-left: 15px;
            color: #555;
        }

        .section-content li::before {
            content: "•";
            position: absolute;
            left: 0;
            color: #22559c;
            font-weight: bold;
        }

        .solutions-text {
            font-size: var(--objectives-font-size);
            line-height: var(--objectives-line-height);
            color: #555;
            margin-bottom: 0.08in;
            padding: 0.05in;
        }

        .service-icons {
            display: flex;
            justify-content: space-around;
            margin: 0.15in 0;
        }

        .service-icon {
            text-align: center;
            flex: 1;
            padding: 0 15px;
        }

        .service-icon-circle {
            width: 72px;
            height: 72px;
            background: #293238;
            border-radius: 50%;
            margin: 0 auto 0.05in;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            color: white;
            box-shadow: 0 4px 15px rgba(41,50,56,0.3);
            border: 2px solid rgba(255,255,255,0.1);
            transition: transform 0.2s ease;
        }

        .service-icon-circle:hover {
            transform: translateY(-2px);
        }

        .service-icon-img {
            width: 60px;
            height: 60px;
            object-fit: contain;
        }

        .service-icon h4 {
            font-size: var(--objectives-font-size);
            font-weight: 600;
            color: #333;
            line-height: 1.2;
        }

        .benefits-list {
            list-style: none;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
            margin-left: -15px;
        }

        .benefits-list li {
            display: flex;
            align-items: flex-start;
            margin-bottom: 0.02in;
            padding-left: 0;
            padding: 0.05in 0;
        }

        .benefits-list li::before {
            display: none;
        }

        .benefit-number {
            width: 67px;
            height: 67px;
            margin-right: 0.1in;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .benefit-block-img {
            width: 67px;
            height: 67px;
            object-fit: contain;
        }

        .benefit-content h4 {
            font-size: var(--outcomes-font-size);
            font-weight: 600;
            color: #333;
            margin-bottom: 0.03in;
            line-height: var(--outcomes-line-height);
        }

        .benefit-content p {
            font-size: var(--outcomes-font-size);
            line-height: var(--outcomes-line-height);
            color: #555;
            margin-bottom: 0;
        }

        .footer {
            background: white;
            color: #333;
            padding: 0 0.3in;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-shrink: 0;
            height: 7%;
        }

        .footer-contact {
            display: flex;
            gap: 85px;
            align-items: center;
        }

        .footer-contact div {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .footer-contact span {
            font-size: 0.9rem;
            color: #555;
            line-height: 1;
            display: flex;
            align-items: center;
        }

        .footer-icon-img {
            height: 24px;
            width: auto;
            object-fit: contain;
            object-position: center;
            display: inline-block;
            vertical-align: middle;
            flex-shrink: 0;
            /* Maintain natural aspect ratio */
            max-height: 24px;
        }
        
        /* Make RISCPoint logo bigger */
        .footer-icon-img[alt="RISCPoint"] {
            height: 30px;
            max-height: 30px;
        }
        
        /* Improve vertical alignment of footer icons with text */
        .footer-contact div {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        
        .footer-contact .icon {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .cirrus-branding {
            text-align: left;
            margin-top: auto;
            margin-bottom: auto;
            flex-shrink: 0;
            height: 12%;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            padding-left: 0.4in;
        }

        .cirrus-logo {
            font-size: 1.5rem;
            font-weight: 700;
            color: #333;
            letter-spacing: 1px;
        }

        .cirrus-logo .md {
            color: #22559c;
        }

        /* Placeholder styles for missing images */
        .placeholder-img {
            background: white;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #999;
            font-size: 10px;
            text-align: center;
        }
        
        /* Page 2 Specific Styles */
        .riscpoint-difference-text {
            font-size: var(--why-choose-riscpoint-font-size);
            line-height: var(--why-choose-riscpoint-line-height);
            color: #555;
            padding: 0.05in;
        }
        
        .business-outcomes-section {
            background: white;
            border-radius: 10px;
            padding: 0.15in;
            margin: 0.1in 0;
            border: 1px solid rgba(34,85,156,0.1);
        }
        
        .business-outcomes-section h4 {
            font-size: var(--partnership-client-overview-font-size);
            font-weight: 600;
            color: #22559c;
            margin-bottom: 0.05in;
            line-height: var(--partnership-client-overview-line-height);
        }
        
        .business-outcomes-section p {
            font-size: var(--partnership-client-overview-font-size);
            line-height: var(--partnership-client-overview-line-height);
            color: #555;
            margin: 0;
        }
        
        .testimonial-section {
            background: white;
            border-left: 4px solid #22559c;
            padding: 0.15in;
            margin: 0.1in 0;
            border-radius: 0 8px 8px 0;
            box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }
        
        .testimonial-quote {
            font-size: var(--testimonial-font-size);
            line-height: var(--testimonial-line-height);
            color: #333;
            font-style: italic;
            margin-bottom: 0.08in;
        }
        
        .testimonial-author {
            font-size: calc(var(--testimonial-font-size) * 0.85);
            color: #666;
            font-weight: 600;
        }
        
.middle-section {
            flex-shrink: 0;
            display: flex;
            align-items: flex-start;
            margin: 0.1in 0;
            overflow: visible; /* allow graphics to extend beyond section */
        }

.contact-section-wrapper {
            margin-top: 0.15in;
            margin-bottom: 0;
            flex-shrink: 0;
        }

        .contact-cta-section {
            background: linear-gradient(135deg, #22559c 0%, #006bff 100%);
            color: white;
            padding: 0.2in;
            border-radius: 12px;
            text-align: center;
            position: relative;
            z-index: 3;
        }
        
        .contact-cta-section h2 {
            font-size: 1.2rem;
            font-weight: 600;
            margin-bottom: 0.1in;
            color: white;
            border-bottom: none;
        }
        
        .contact-cta-section p {
            font-size: var(--call-to-action-font-size);
            line-height: var(--call-to-action-line-height);
            margin: 0;
        }
        
        /* Two-column layout for Why Choose RISCPoint and Contact CTA */
        .two-column-section {
            display: flex;
            gap: 0.2in;
            align-items: stretch; /* ensure equal column heights */
        }
        
        .left-column {
            flex: 0 0 auto; /* prevent flex growth/shrink */
            width: 40%;
            min-width: 40%;
            max-width: 40%;
            background: white;
        }
        
        .right-column {
            flex: 1;
            display: flex;
            align-items: stretch;
        }

        .right-column-content {
            flex: 0 0 auto; /* prevent flex growth/shrink */
            width: 60%;
            min-width: 60%;
            max-width: 60%;
            display: flex;
            flex-direction: column;
        }
        
        .right-column .contact-cta-section {
            margin-top: 0;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
        }
        
        /* Placeholder graphics area styles */
        .placeholder-graphics-area {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.2in;
            height: 2.2in;
            max-height: 2.2in;
            overflow: hidden;
        }
        
        .placeholder-rectangle {
            width: 100%;
            height: 100%;
            background: white;
            border: 2px dashed #22559c;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #666;
            font-size: 1rem;
            font-weight: 500;
        }
        
        /* Graphics logos container - single shared viewport */
        .graphics-logos-container {
            width: 100%;
            height: 200px;
            background: transparent;
            position: relative;
            overflow: hidden;
        }
        
        .graphics-logo-slot {
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            pointer-events: none;
        }
        
        .graphics-logo-slot img {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            transform-origin: center center;
            object-fit: contain;
            display: block;
        }
        
        .graphics-logo-1 img {
            transform: translate(-50%, -50%);
        }
        .graphics-logo-2 img {
            transform: translate(-50%, calc(-50% + 80px));
        }

        /* Injected graphics and partner images should stay behind text and CTA */
        .graphics-injected-img,
        .injected-partner-img {
            z-index: 1 !important;
        }
        
    </style>
</head>
<body>
  <div class="container">
    <!-- Page 2 has no header - starts directly with content -->

    <main class="main-content">
${page2Html}
    </main>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-contact">
        <div>
          <div class="icon">
            <img src="${templateImagesBaseUrl}/small_logo.png" alt="RISCPoint" class="footer-icon-img">
          </div>
          <span>www.RISCPoint.com</span>
        </div>
        <div>
          <div class="icon">
            <img src="${templateImagesBaseUrl}/email_logo.png" alt="Email" class="footer-icon-img">
          </div>
          <span>info@riscpoint.com</span>
        </div>
        <div>
          <div class="icon">
            <img src="${templateImagesBaseUrl}/phone_logo.png" alt="Phone" class="footer-icon-img">
          </div>
          <span>(888) 320-1327</span>
        </div>
      </div>
    </footer>
  </div>
</body>
</html>`;
};

export default renderPage2Template;
